import {
	Children,
	cloneElement,
	Fragment,
	FragmentProps,
	isValidElement,
	ReactNode,
	Key,
	Ref,
	RefObject,
	ReactElement
} from "react";

export function getReactNodeKey(child: ReactNode, indexInArray: number): string {
	let subKey: Key | null = null;

	if (typeof child === "object" && child !== null && "key" in child) {
		subKey = child.key;
	}

	if (!subKey) {
		subKey = `index-${indexInArray}`;
	}

	return `${subKey}`;
}

export function outOfContextCall(contextName: string, methodName: string): () => void {
	return () => console.warn(`Method ${methodName} from ${contextName} context was called, but the context was not initialized.`);
}

export function outOfContextCallError(contextName: string, methodName: string): () => never {
	return (): never => {
		throw new Error(`Method ${methodName} from ${contextName} context was called, but the context was not initialized.`);
	};
}

export function setRef<T>(
	ref: Ref<T>,
	value: T | null
) {
	if (typeof ref === "function") {
		ref(value);
	} else {
		(ref as RefObject<T | null>).current = value;
	}
}

export function mergeRefs<T>(
	...refs: Ref<T>[]
): React.RefCallback<T> {
	return (value) => {
		for (const ref of refs) {
			if (!ref) {
				continue;
			}

			setRef(ref, value);
		}
	};
}

export function flattenChildren<TProps>(children: ReactElement<TProps> | ReactElement<TProps>[]): ReactElement<TProps>[];
export function flattenChildren(children: ReactNode): ReactNode[];
export function flattenChildren(children: ReactNode): ReactNode[] {
	const result: ReactNode[] = [];
	flattenChildrenInternal(
		children,
		[],
		result
	);
	return result;
}

export function hasImplicitDependencyOn(
	...dependencies: readonly unknown[]
): void {
	void (dependencies);
}


function isFragment(node: ReactNode): node is ReactElement<FragmentProps> {
	return (node as ReactElement).type === Fragment;
}

function flattenChildrenInternal(
	children: ReactNode,
	keyStack: (string | number)[],
	result: ReactNode[],
): void{
	const directChildren: ReactNode[] = Children.toArray(children);
	for (let i = 0; i < directChildren.length; i++) {
		const child: ReactNode = directChildren[i];
		if (isFragment(child)) {
			flattenChildrenInternal(
				child.props.children,
				[...keyStack, child.key || i],
				result
			);
		} else if (isValidElement(child)) {
			result.push(
				cloneElement(
					child,
					{
						key: [...keyStack, (child.key || i)].join(".")
					}
				)
			);
		} else {
			result.push(child);
		}
	}
}